
import { defineComponent, getCurrentInstance, Ref, ref, watch } from "vue";
import { apiService } from "@services/api.service";
import { ciTrackingService } from "@services/ci-tracking.service";
import { useI18n } from "@composables/i18n";
import { useConversations } from "@composables/conversations";
import { fullAddress } from "@utils/user-helpers";
import CiUserAvatar from "@components/base/avatar.vue";

const Animation = () => import("@components/animation/animation.vue");

enum State {
  Start,
  Sending,
}

export default defineComponent({
  name: "CiDirectMessageModal",
  components: { CiUserAvatar, CapAnimation: Animation },
  props: {
    userId: { type: Number, default: 0 },
    modalId: { type: String, default: "modal-direct-message" },
  },
  emits: ["animation-complete"],
  setup(props, { emit }) {
    const root = getCurrentInstance()?.proxy.$root;
    const text = ref("");
    const state = ref(State.Start);
    const maxProgressValue = ref(100);
    const progressValue = ref(0);
    const animationName = ref("");
    const userLoaded = ref(false);
    const userNotFound = ref(false);
    const user: Ref<Users.Search> = ref({
      id: 0,
      name: "",
      profile_link: "",
      avatar_url: null,
      position: "",
      company_name: "",
    });
    const conversation: Ref<Nullable<Conversations.Conversation>> = ref(null);

    async function setData() {
      if (user.value.id !== props.userId) {
        try {
          const response = await apiService.getUser(props.userId);
          user.value = response.data.user;
          userLoaded.value = true;
        } catch (error) {
          userNotFound.value = true;
        }
      }

      if (!conversation.value) {
        try {
          const response = await apiService.conversations.conversation(user.value.id);
          conversation.value = response.data.conversation;
        } catch (error) {
          console.warn(`No conversation with User id[${props.userId}] could be loaded.\n${error}`);
        }
      }
    }

    async function send() {
      if (text.value.length > 0) {
        const { createMessage, createConversation, currentConversation } = useConversations();
        if (conversation.value) {
          await createMessage(conversation.value.id!, text.value);
        } else {
          conversation.value = await createConversation(text.value, user.value.id);
        }

        await ciTrackingService.track({
          event: { action: "send", value: conversation.value?.id || currentConversation.value?.id },
          target: { name: "Message", identifier: props.userId.toString(), url: user.value.profile_link },
          source: { name: null, identifier: window.location.href },
        });
        updateAnimationName();
        state.value = State.Sending;
      }
    }

    function updateProgress(payload: { totalTime: number; currentTime: number }) {
      maxProgressValue.value = payload.totalTime;
      if (payload.currentTime % 3 < 1) {
        progressValue.value = payload.currentTime;
      }
    }

    const animationComplete = () => {
      emit("animation-complete");
      root!.$bvModal.hide(props.modalId);
    };

    function reset() {
      text.value = "";
      state.value = State.Start;
      progressValue.value = 0;
      maxProgressValue.value = 100;
    }

    function userAddress(user: Users.Search) {
      return fullAddress(user);
    }

    function updateAnimationName() {
      animationName.value = "checkmark-circle--animated";
    }

    watch(
      () => props.userId,
      () => {
        setData();
      }
    );

    return {
      text,
      state,
      maxProgressValue,
      progressValue,
      animationName,
      userLoaded,
      userNotFound,
      user,
      dataConversation: conversation,
      send,
      updateProgress,
      animationComplete,
      setData,
      reset,
      userAddress,
    };
  },
  computed: {
    sendEnabled(): boolean {
      return this.text.length > 0;
    },
    sendPossibleClass(): string {
      return this.sendEnabled ? "active" : "";
    },
    hideBorderClass(): string {
      return this.showAnimation ? "hide" : "";
    },
    title() {
      const { t } = useI18n(getCurrentInstance());
      return t("conversations.direct.title", { name: this.user.name });
    },
    showAnimation(): boolean {
      return this.state === State.Sending;
    },
    showSendText(): boolean {
      return this.progressValue / this.maxProgressValue > 0.5;
    },
    sendTextClass(): string {
      return this.showSendText ? "direct-message__visible" : "";
    },
    headerClass(): string[] {
      return this.showAnimation || !this.userLoaded ? ["pb-0", "direct-message__transparent"] : ["pb-0"];
    },
  },
});
